import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@mui/material';

import { type IndicatorMetadata,indicatorRegistry } from '../../../services/indicatorRegistry';
import {
  getViewedIndicators,
  INDICATOR_VIEWED_EVENT,
  markIndicatorViewed,
} from '../../../services/indicatorViewedState';

interface NextIndicatorButtonProps {
  /**
   * Jotdoc ID for viewed state persistence
   */
  jotdocId: string;
}

/**
 * Global "Next" button that navigates to the next unviewed indicator
 * Positioned sticky at the bottom of the left gutter area
 */
const NextIndicatorButton: React.FC<NextIndicatorButtonProps> = ({ jotdocId }) => {
  const [unviewedIndicators, setUnviewedIndicators] = useState<IndicatorMetadata[]>([]);
  const [viewedIds, setViewedIds] = useState<Set<string>>(new Set());
  const [activeIndicatorId, setActiveIndicatorId] = useState<string | null>(null);

  // Update unviewed indicators list
  const updateUnviewedIndicators = useCallback((viewed: Set<string>) => {
    const unviewed = indicatorRegistry.getUnviewed(viewed);
    setUnviewedIndicators(unviewed);
  }, []);

  // Load viewed indicators
  const loadViewedIndicators = useCallback(async () => {
    const viewed = await getViewedIndicators(jotdocId);
    setViewedIds(viewed);
    updateUnviewedIndicators(viewed);
  }, [jotdocId, updateUnviewedIndicators]);

  useEffect(() => {
    loadViewedIndicators();
  }, [loadViewedIndicators]);

  // Listen for indicator viewed events
  useEffect(() => {
    const handleIndicatorViewed = (event: Event) => {
      const customEvent = event as CustomEvent<{ jotdocId: string; indicatorId: string }>;
      if (customEvent.detail.jotdocId === jotdocId) {
        // Reload viewed indicators when one is marked as viewed
        loadViewedIndicators();
      }
    };

    window.addEventListener(INDICATOR_VIEWED_EVENT, handleIndicatorViewed as EventListener);
    return () => {
      window.removeEventListener(INDICATOR_VIEWED_EVENT, handleIndicatorViewed as EventListener);
    };
  }, [jotdocId, loadViewedIndicators]);

  // Subscribe to registry changes
  useEffect(() => {
    const unsubscribe = indicatorRegistry.subscribe(() => {
      updateUnviewedIndicators(viewedIds);
    });

    return unsubscribe;
  }, [viewedIds, updateUnviewedIndicators]);

  // Update when viewedIds change
  useEffect(() => {
    updateUnviewedIndicators(viewedIds);
  }, [viewedIds, updateUnviewedIndicators]);

  // Handle click - scroll to next unviewed indicator
  const handleNext = useCallback(async () => {
    if (unviewedIndicators.length === 0) return;

    // Get the first unviewed indicator
    const nextIndicator = unviewedIndicators[0];

    if (nextIndicator && nextIndicator.element) {
      // Close previous indicator's popover/modal if there is one
      if (activeIndicatorId && activeIndicatorId !== nextIndicator.id) {
        const previousIndicator = indicatorRegistry.getById(activeIndicatorId);
        if (previousIndicator?.onDeactivate) {
          previousIndicator.onDeactivate();
        }
      }

      // Scroll to the indicator element
      nextIndicator.element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });

      // Wait 800ms for scroll animation and indicator visibility delay
      await new Promise(resolve => setTimeout(resolve, 800));

      // Trigger the indicator's action (show popover/modal)
      if (nextIndicator.onActivate) {
        nextIndicator.onActivate();
      }

      // Update active indicator ID
      setActiveIndicatorId(nextIndicator.id);

      // Auto-mark as viewed after triggering action
      await markIndicatorViewed(jotdocId, nextIndicator.id);

      // Update local state
      setViewedIds(prev => new Set(prev).add(nextIndicator.id));
      updateUnviewedIndicators(new Set([...viewedIds, nextIndicator.id]));
    }
  }, [unviewedIndicators, jotdocId, viewedIds, updateUnviewedIndicators, activeIndicatorId]);

  // Don't render if there are no unviewed indicators
  if (unviewedIndicators.length === 0) {
    return null;
  }

  return (
    <Button
      variant="contained"
      onClick={handleNext}
      sx={{
        minWidth: 100,
        maxWidth: 160,
        width: 160,
        height: 40,
        fontWeight: 600,
        marginRight: 8,
        fontSize: '0.875rem',
        px: 1.5,
        boxShadow: 3,
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      Next ({unviewedIndicators.length})
    </Button>
  );
};

export default NextIndicatorButton;
